import { uninterceptedApi } from "@/lib/api";
import { APP_TYPE } from "@/lib/constants";
import { ErrorResponse } from "@/lib/types";
import { LoginForm } from "@/pages/auth/login";
import { RegisterForm } from "@/pages/auth/register";
import { useMutation, UseMutationOptions } from "@tanstack/react-query";

export interface LoginResponse {
  accessToken: string;
  expiresIn: number;
  notBeforePolicy: number;
  refreshExpiresIn: number;
  refreshToken: string;
  scope: string;
  sessionState: string;
  tokenType: string;
}

// Login response for a user with two-factor on: no tokens until the code is verified.
export interface MfaChallengeResponse {
  mfaRequired: true;
  challengeId: string;
  method: string;
  // Email method only: the masked address the code went to, and seconds until a resend is allowed.
  emailHint?: string | null;
  resendAvailableInSeconds?: number | null;
  // Email method only: set when no code went out (hourly cap hit, or the send failed).
  emailError?: "email_limit" | "email_send_failed" | null;
}

export const isMfaChallenge = (
  response: LoginResponse | MfaChallengeResponse
): response is MfaChallengeResponse => "mfaRequired" in response;

export const useLogin = (props?: {
  onSuccess?: (data: { data: LoginResponse | MfaChallengeResponse }) => void;
  onError?: (error: ErrorResponse) => void;
}) => {
  return useMutation<
    { data: LoginResponse | MfaChallengeResponse },
    ErrorResponse,
    LoginForm
  >({
    mutationFn: (data: LoginForm) => uninterceptedApi.post("/auth/login", data),
    ...props,
  });
};

export interface TwoFactorLoginRequest {
  challengeId: string;
  code?: string;
  recoveryCode?: string;
}

export const useLoginTwoFactor = (props?: {
  onSuccess?: (data: { data: LoginResponse }) => void;
  onError?: (error: ErrorResponse) => void;
}) => {
  return useMutation<
    { data: LoginResponse },
    ErrorResponse,
    TwoFactorLoginRequest
  >({
    mutationFn: (data: TwoFactorLoginRequest) =>
      uninterceptedApi.post("/auth/login/2fa", data),
    ...props,
  });
};

export interface ResendCodeResponse {
  resendAvailableInSeconds: number;
  resendsRemaining: number;
}

export const useResendLoginCode = (props?: {
  onSuccess?: (data: { data: ResendCodeResponse }) => void;
  onError?: (error: ErrorResponse) => void;
}) => {
  return useMutation<{ data: ResendCodeResponse }, ErrorResponse, string>({
    mutationFn: (challengeId: string) =>
      uninterceptedApi.post("/auth/login/2fa/resend", { challengeId }),
    ...props,
  });
};

export const useRegister = (props?: {
  onSuccess?: () => void;
  onError?: (error: ErrorResponse) => void;
}) => {
  return useMutation({
    mutationFn: (data: RegisterForm) =>
      uninterceptedApi.post("/auth/register", data),
    ...props,
  });
};

export interface ForgotPasswordResponse {
  message: string;
}

export type AppType = keyof typeof APP_TYPE;

export interface ForgotPasswordRequest {
  email: string;
  app?: AppType;
}

export const useForgotPassword = (
  options?: UseMutationOptions<
    ForgotPasswordResponse,
    Error,
    ForgotPasswordRequest
  >
) => {
  return useMutation<ForgotPasswordResponse, Error, ForgotPasswordRequest>({
    mutationKey: ["forgot-password"],
    mutationFn: async (data: ForgotPasswordRequest) => {
      const response = await uninterceptedApi.post<ForgotPasswordResponse>(
        "/auth/forgot-password",
        data
      );
      return response.data;
    },
    ...options,
  });
};

export interface ResetPasswordResponse {
  message: string;
}

export interface ResetPasswordRequest {
  token: string;
  newPassword: string;
}

export const useResetPassword = (
  options?: UseMutationOptions<
    ResetPasswordResponse,
    Error,
    ResetPasswordRequest
  >
) => {
  return useMutation<ResetPasswordResponse, Error, ResetPasswordRequest>({
    mutationKey: ["reset-password"],
    mutationFn: async (data: ResetPasswordRequest) => {
      const response = await uninterceptedApi.post<ResetPasswordResponse>(
        "/auth/reset-password-confirm",
        data
      );
      return response.data;
    },
    ...options,
  });
};

export interface LogoutForm {
  refreshToken: string;
}

export const useLogout = (props?: {
  onSuccess?: () => void;
  onError?: (error: ErrorResponse) => void;
}) => {
  return useMutation({
    mutationFn: (data: LogoutForm) =>
      uninterceptedApi.post("/auth/logout", data),
    ...props,
  });
};

export interface RefreshTokenForm {
  refreshToken: string;
}

export interface RefreshTokenResponse {
  accessToken: string;
  expiresIn: number;
  refreshExpiresIn: number;
  refreshToken: string;
  tokenType: string;
  notBeforePolicy: number;
  sessionState: string;
  scope: string;
}

export const useRefreshToken = (props?: {
  onSuccess?: (data: { data: RefreshTokenResponse }) => void;
  onError?: (error: ErrorResponse) => void;
}) => {
  return useMutation<
    { data: RefreshTokenResponse },
    ErrorResponse,
    RefreshTokenForm
  >({
    mutationFn: (data: RefreshTokenForm) =>
      uninterceptedApi.post("/auth/refresh", data),
    ...props,
  });
};
